import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FEEXPAY_COUNTRIES,
  XOF_RATE_CURRENCIES,
  buildFeexPayUrl,
  buildFeexPayCardUrl,
  defaultXofRate,
  type FeexPayConfig,
  type FeexPayNetworkOption,
  resolveFeexPayNetworks,
} from '@/lib/feexpay';

interface FeexPaySettingsPanelProps {
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}

const inputClass =
  'w-full h-9 px-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300';

export default function FeexPaySettingsPanel({ config, onPatch }: FeexPaySettingsPanelProps) {
  const feexConfig = config as FeexPayConfig;
  const networks = useMemo(() => resolveFeexPayNetworks(feexConfig), [feexConfig]);
  const apiUrl = String(config.api_url || '');

  // Groupement par pays en conservant l'ordre d'insertion.
  const grouped = useMemo(() => {
    const map = new Map<string, { label: string; items: FeexPayNetworkOption[] }>();
    networks.forEach((op) => {
      if (!map.has(op.country)) map.set(op.country, { label: op.countryLabel, items: [] });
      map.get(op.country)!.items.push(op);
    });
    return Array.from(map.entries());
  }, [networks]);

  const writeNetworks = (list: FeexPayNetworkOption[]) => {
    onPatch({
      networks: list.map((o) => ({
        country: o.country,
        slug: o.slug,
        label: o.label,
        enabled: o.enabled,
      })),
    });
  };

  const updateOperator = (country: string, slug: string, patch: Partial<FeexPayNetworkOption>) => {
    writeNetworks(networks.map((o) => (o.country === country && o.slug === slug ? { ...o, ...patch } : o)));
  };

  const removeOperator = (country: string, slug: string) => {
    writeNetworks(networks.filter((o) => !(o.country === country && o.slug === slug)));
  };

  const addOperator = (country: string) => {
    writeNetworks([
      ...networks,
      { country, countryLabel: FEEXPAY_COUNTRIES.find((c) => c.code === country)?.label || country, slug: '', label: 'Nouvel opérateur', enabled: true, fromCatalog: false },
    ]);
  };

  const activeCountries = Array.from(new Set(networks.filter((o) => o.enabled).map((o) => o.country)));
  const defaultCountry = String(config.country || '') || activeCountries[0] || '';
  const operatorsOfDefault = networks.filter((o) => o.country === defaultCountry && o.enabled);

  // Taux de conversion par devise (1 unité → XOF)
  const rateMap = (feexConfig.xof_rates || {}) as Record<string, number | string>;
  const rateCodes = useMemo(() => Object.keys(rateMap), [rateMap]);
  const missingRates = useMemo(() => XOF_RATE_CURRENCIES.filter((c) => !rateCodes.includes(c)), [rateCodes]);

  const updateRate = (code: string, value: string) => {
    onPatch({ xof_rates: { ...rateMap, [code]: value } });
  };

  const removeRate = (code: string) => {
    const next = { ...rateMap };
    delete next[code];
    onPatch({ xof_rates: next });
  };

  const addRate = (code: string) => {
    if (!code || rateMap[code] !== undefined) return;
    onPatch({ xof_rates: { ...rateMap, [code]: String(defaultXofRate(code)) } });
  };

  return (
    <div className="mt-4 rounded-lg border border-accent-200/60 bg-accent-50/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-global-line text-accent-600"></i>
        </div>
        <div>
          <p className="text-xs font-bold text-foreground-900">Pays & opérateurs Mobile Money</p>
          <p className="text-[11px] text-foreground-500">
            FeexPay v2 appelle une URL par opérateur. Chaque identifiant ci-dessous est éditable selon votre doc FeexPay.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Pays par défaut</label>
          <select
            value={defaultCountry}
            onChange={(e) => onPatch({ country: e.target.value, network: '' })}
            className={`${inputClass} cursor-pointer`}
          >
            <option value="">— Choisir —</option>
            {FEEXPAY_COUNTRIES.filter((c) => activeCountries.includes(c.code)).map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-foreground-600 mb-1">Opérateur par défaut</label>
          <select
            value={String(config.network || '')}
            onChange={(e) => onPatch({ network: e.target.value })}
            className={`${inputClass} cursor-pointer`}
            disabled={!defaultCountry}
          >
            <option value="">— Choisir —</option>
            {operatorsOfDefault.map((o) => (
              <option key={`${o.country}-${o.slug}`} value={o.slug}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-foreground-600 mb-1">
            Taux par défaut → XOF / XAF
          </label>
          <input
            type="number"
            min="1"
            value={String(config.xof_rate || '')}
            onChange={(e) => onPatch({ xof_rate: e.target.value })}
            placeholder="64"
            className={inputClass}
          />
          <p className="text-[10px] text-foreground-400 mt-1">Utilisé si aucune devise n’a de taux dédié ci-dessous.</p>
        </div>
      </div>

      {/* Taux de conversion XOF par devise */}
      <div className="mb-4 rounded-lg border border-background-200/70 bg-background-50 p-3">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground-800 flex items-center gap-1.5">
              <i className="ri-exchange-dollar-line text-accent-600"></i>
              Taux de conversion par devise → XOF / XAF
            </p>
            <p className="text-[10px] text-foreground-400 mt-0.5">
              Le pays encaissé définit la devise réelle : XOF en Afrique de l’Ouest, XAF en Afrique centrale
              (parité fixe 1:1). Montant envoyé = montant de la commande × taux de sa devise.
            </p>
          </div>
          <select
            value=""
            onChange={(e) => { if (e.target.value) addRate(e.target.value); }}
            className="h-8 px-2 rounded-lg border border-background-200/70 bg-background-50 text-[11px] text-foreground-700 focus:outline-none cursor-pointer flex-shrink-0"
          >
            <option value="">+ Ajouter une devise</option>
            {missingRates.map((code) => (
              <option key={code} value={code}>{code} ({defaultXofRate(code)})</option>
            ))}
          </select>
        </div>

        {rateCodes.length === 0 ? (
          <p className="text-[11px] text-foreground-400">
            Aucune devise dédiée : le taux par défaut ({String(config.xof_rate || 'repli automatique')}) s’applique à toutes les devises.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
            {rateCodes.map((code) => (
              <div key={code} className="flex items-center gap-2 rounded-lg border border-background-200/70 bg-background-50 px-2 py-1.5">
                <span className="text-[11px] font-bold text-foreground-800 w-12 flex-shrink-0">{code}</span>
                <input
                  type="number"
                  min="0"
                  step="0.0001"
                  value={String(rateMap[code] ?? '')}
                  onChange={(e) => updateRate(code, e.target.value)}
                  placeholder={String(defaultXofRate(code))}
                  className="flex-1 min-w-0 h-7 px-2 rounded-md border border-background-200/70 bg-background-50 text-[11px] text-foreground-900 focus:outline-none focus:border-primary-300"
                />
                <span className="text-[10px] text-foreground-400 flex-shrink-0">XOF/XAF</span>
                <button
                  type="button"
                  onClick={() => removeRate(code)}
                  className="w-6 h-6 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                  aria-label={`Supprimer ${code}`}
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {grouped.map(([country, group]) => (
          <div key={country} className="rounded-lg border border-background-200/70 bg-background-50 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground-800">{group.label}</span>
              <button
                type="button"
                onClick={() => addOperator(country)}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-accent-700 bg-accent-100 hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Ajouter
              </button>
            </div>
            <div className="space-y-2">
              {group.items.map((op) => (
                <div key={`${op.country}-${op.slug}`} className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => updateOperator(op.country, op.slug, { enabled: !op.enabled })}
                    className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                      op.enabled ? 'bg-accent-500' : 'bg-background-200'
                    }`}
                    aria-label={op.enabled ? 'Désactiver' : 'Activer'}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${op.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <input
                    type="text"
                    value={op.label}
                    onChange={(e) => updateOperator(op.country, op.slug, { label: e.target.value })}
                    placeholder="Nom de l’opérateur"
                    className="w-40 h-9 px-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                  <div className="flex items-center gap-1 flex-1 min-w-[220px]">
                    <span className="text-[10px] text-foreground-400 font-mono whitespace-nowrap">/requesttopay/</span>
                    <input
                      type="text"
                      value={op.slug}
                      onChange={(e) => updateOperator(op.country, op.slug, { slug: e.target.value.trim() })}
                      placeholder="mtn"
                      className="w-24 h-9 px-2.5 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 font-mono focus:outline-none focus:border-primary-300"
                    />
                    <span className="text-[10px] text-foreground-400 truncate hidden lg:block" title={buildFeexPayUrl(apiUrl, op.slug)}>
                      {buildFeexPayUrl(apiUrl, op.slug)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOperator(op.country, op.slug)}
                    className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                    aria-label="Supprimer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Paiement par carte bancaire (Card collection) */}
      <div className="mt-4 rounded-lg border border-background-200/70 bg-background-50 p-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onPatch({ card_enabled: !feexConfig.card_enabled })}
            className={`relative inline-flex h-6 w-10 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
              feexConfig.card_enabled ? 'bg-primary-500' : 'bg-background-200'
            }`}
            aria-label={feexConfig.card_enabled ? 'Désactiver la carte' : 'Activer la carte'}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${feexConfig.card_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
          </button>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-foreground-800">Paiement par carte bancaire</p>
            <p className="text-[11px] text-foreground-500">En complément du Mobile Money (collection carte FeexPay).</p>
          </div>
        </div>
        {feexConfig.card_enabled && (
          <div className="mt-3">
            <label className="block text-[11px] font-semibold text-foreground-600 mb-1">URL de collecte carte (facultatif)</label>
            <input
              type="text"
              value={String(config.card_url || '')}
              onChange={(e) => onPatch({ card_url: e.target.value })}
              placeholder={buildFeexPayCardUrl(apiUrl, '')}
              className={inputClass}
            />
            <p className="text-[10px] text-foreground-400 mt-1 truncate" title={buildFeexPayCardUrl(apiUrl, String(config.card_url || ''))}>
              Endpoint utilisé : {buildFeexPayCardUrl(apiUrl, String(config.card_url || ''))}
            </p>
          </div>
        )}
      </div>

      <div className="mt-3 rounded-lg border border-background-200/70 bg-background-50 px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[10px] text-foreground-500 flex items-start gap-1.5 min-w-0 flex-1">
          <i className="ri-webhook-line text-accent-500 mt-0.5"></i>
          <span>
            Le webhook <span className="font-mono">…/functions/v1/feexpay-webhook</span> reçoit les confirmations
            FeexPay et journalise chaque appel. La page dédiée donne l’URL exacte, l’en-tête et un bouton de test.
          </span>
        </p>
        <Link
          to="/superadmin/webhooks/feexpay"
          className="h-8 px-3 rounded-full bg-foreground-950 text-background-50 text-[11px] font-semibold whitespace-nowrap flex items-center gap-1.5 hover:bg-foreground-800 transition-colors flex-shrink-0"
        >
          <i className="ri-settings-3-line"></i>
          Configurer le webhook
        </Link>
      </div>    </div>
  );
}